// src/features/stock-ranking/api.ts
//
// Data fetcher for the Equities ranking page. Talks to the FastAPI
// ranking router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the endpoint is reached only as an authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/ranking/top-assets` path here — adding
// `/api/v1` would double the prefix. See api/client.ts (REQUIRED_API_PREFIX).

import { apiClient } from '../../api/client';
import type { RankingRow } from '../../shared/ui/RankingTable';

export interface TopAssetsResponse {
    as_of_date: string;
    top_n: number;
    rankings: RankingRow[];
}

export const fetchTopAssets = async (): Promise<TopAssetsResponse> => {
    // Resolves to GET /api/v1/ranking/top-assets (prefix from baseURL).
    const { data } = await apiClient.get<TopAssetsResponse>(
        '/ranking/top-assets',
    );
    return data;
};

// --- Full universe (~220 symbols, non-paginated) -------------------------
// The backend returns the entire cross-section for the latest date in one
// payload (~30-50 KB gzip'd). We filter + sort it entirely client-side, so
// sector-tab and column-sort clicks never hit the network again.

// A universe row carries the same public columns as a leaderboard row.
export type UniverseRow = RankingRow;

export interface UniverseResponse {
    as_of_date: string;
    total: number;
    items: UniverseRow[];
}

export const fetchUniverse = async (
    sector?: string,
): Promise<UniverseResponse> => {
    // Resolves to GET /api/v1/ranking/universe (prefix from baseURL).
    const { data } = await apiClient.get<UniverseResponse>('/ranking/universe', {
        params: sector ? { sector } : undefined,
    });
    return data;
};

// --- Batched historical trajectories (sparklines) ------------------------
// One request for many symbols collapses what would be an N+1 fetch
// waterfall (one call per visible table row) into a single round-trip.

export interface HistoryPoint {
    date: string;
    forge_ultra_signal: number;
}

// Map of {symbol: [{date, forge_ultra_signal}, ...]}. Every requested
// symbol is present as a key (empty array if it has no rows in window).
export type HistoricalBatchResponse = Record<string, HistoryPoint[]>;

export const fetchHistoricalBatch = async (
    symbols: string[],
    days = 90,
): Promise<HistoricalBatchResponse> => {
    if (symbols.length === 0) return {};
    const { data } = await apiClient.get<HistoricalBatchResponse>(
        '/ranking/historical-batch',
        { params: { symbols: symbols.join(','), days } },
    );
    return data;
};

// --- Sector summary (radar / heat strip) ---------------------------------
// Per-sector average factor z-scores, aggregated server-side over the
// latest date so the client receives ~11 rows instead of all 220.

export interface SectorSummaryRow {
    sector: string;
    count: number;
    avg_momentum_z: number;
    avg_fundamental_z: number;
    avg_sentiment_z: number;
    avg_signal: number;
}

export interface SectorSummaryResponse {
    as_of_date: string;
    sectors: SectorSummaryRow[];
}

export const fetchSectorSummary = async (): Promise<SectorSummaryResponse> => {
    // Resolves to GET /api/v1/ranking/sector-summary (prefix from baseURL).
    const { data } = await apiClient.get<SectorSummaryResponse>(
        '/ranking/sector-summary',
    );
    return data;
};

// --- Quick stats (symbol pop-over) ---------------------------------------
// Latest close price + market cap for a single symbol. Powers the
// click-to-open symbol pop-over on the universe table. We go through the
// shared `apiClient` (NOT raw fetch) so the WP JWT is attached — the
// /ranking router is auth-gated, so an unauthenticated fetch would 401.
//
// `last_price` and `market_cap` are nullable: a recently-listed or
// foreign-domiciled name can have pricing but no filed fundamentals, in
// which case the backend returns null and the UI renders "N/A".

export interface QuickStats {
    symbol: string;
    company_name: string;
    sector: string | null;
    last_price: number | null;
    market_cap: number | null;
}

export const fetchQuickStats = async (
    symbol: string,
): Promise<QuickStats> => {
    // Resolves to GET /api/v1/ranking/quick-stats/<symbol>.
    const { data } = await apiClient.get<QuickStats>(
        `/ranking/quick-stats/${encodeURIComponent(symbol)}`,
    );
    return data;
};

// --- Per-symbol detail pop-over fetchers (StockDetailPopup) -------------
// Single-symbol trailing-window series sourced from the
// /ranking/{price-history,factors,signal,overview} endpoints. The three
// series endpoints are Redis-cached server-side and always return
// {symbol, count, series} (series may be empty -> render an empty-state,
// never a 404 unless the nightly chain has never run). The price series is
// raw OHLCV from asset_daily_pricing; the factor / signal series come from
// asset_daily_rankings.

export interface PriceBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number | null;
}

export interface PriceHistoryResponse {
    symbol: string;
    count: number;
    series: PriceBar[];
}

export const fetchPriceHistory = async (
    symbol: string,
    days = 365,
): Promise<PriceHistoryResponse> => {
    const { data } = await apiClient.get<PriceHistoryResponse>(
        `/ranking/price-history/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

export interface FactorPoint {
    date: string;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    annualized_volatility: number;
    forge_ultra_signal: number;
}

export interface FactorsResponse {
    symbol: string;
    count: number;
    series: FactorPoint[];
}

export const fetchFactors = async (
    symbol: string,
    days = 365,
): Promise<FactorsResponse> => {
    const { data } = await apiClient.get<FactorsResponse>(
        `/ranking/factors/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

export interface SignalPoint {
    date: string;
    forge_ultra_signal: number;
}

export interface SignalResponse {
    symbol: string;
    count: number;
    series: SignalPoint[];
}

export const fetchSignal = async (
    symbol: string,
    days = 365,
): Promise<SignalResponse> => {
    const { data } = await apiClient.get<SignalResponse>(
        `/ranking/signal/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

// One-shot latest snapshot for the popup header stats (no chart). Returns
// 404 for a symbol not in the latest ranked cross-section, so callers should
// treat its failure as best-effort (the charts must still render).
export interface OverviewResponse {
    symbol: string;
    name: string;
    as_of_date: string;
    sector: string;
    universe_rank: number;
    sector_rank: number;
    forge_ultra_signal: number;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    annualized_volatility: number;
}

export const fetchOverview = async (
    symbol: string,
): Promise<OverviewResponse> => {
    const { data } = await apiClient.get<OverviewResponse>(
        `/ranking/overview/${encodeURIComponent(symbol)}`,
    );
    return data;
};
